// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import breakpoints from "@awc/XDL/PreStyle/breakpoints";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import * as cssLinter from "css";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    extraLargeColumnCount = "extraLargeColumnCount",
    extraLargeRowCount = "extraLargeRowCount",
    largeColumnCount = "largeColumnCount",
    mediumColumnCount = "mediumColumnCount",
    smallColumnCount = "smallColumnCount",
    extraSmallColumnCount = "extraSmallColumnCount",
    isFullWidth = "isFullWidth",
    customCSS = "customCSS",
}

interface Props extends WidgetProps {
    fields: {
        [fields.extraLargeColumnCount]: GridWidths;
        [fields.extraLargeRowCount]: GridWidths;
        [fields.largeColumnCount]: GridWidths;
        [fields.mediumColumnCount]: GridWidths;
        [fields.smallColumnCount]: GridWidths;
        [fields.extraSmallColumnCount]: GridWidths;
        [fields.isFullWidth]: boolean;
        [fields.customCSS]: string;
    };
}

const getColumnGridUnits = (numberOfColumns: number) => {
    return Math.floor(12 / Math.min(Math.max(numberOfColumns, 1), 12)) as GridWidths;
};

const Grid = ({ fields, id }: Props) => {
    const extraLargeColumnWidth = getColumnGridUnits(fields.extraLargeColumnCount);
    const largeColumnWidth = getColumnGridUnits(fields.largeColumnCount);
    const mediumColumnWidth = getColumnGridUnits(fields.mediumColumnCount);
    const smallColumnWidth = getColumnGridUnits(fields.smallColumnCount);
    const extraSmallColumnWidth = getColumnGridUnits(fields.extraSmallColumnCount);

    const rows: JSX.Element[] = [];
    for (let row = 0; row < fields.extraLargeRowCount; row = row + 1) {
        const columns: JSX.Element[] = [];
        for (let column = 0; column < fields.extraLargeColumnCount; column = column + 1) {
            columns.push(
                <GridItem
                    key={`GridItem-${id}-${column}-${row}`}
                    width={[
                        extraSmallColumnWidth,
                        smallColumnWidth,
                        mediumColumnWidth,
                        largeColumnWidth,
                        extraLargeColumnWidth,
                    ]}
                >
                    <Zone contentId={id} zoneName={`Content${column}${row}`} />
                </GridItem>,
            );
        }
        const rowCss = css`
            ${fields.customCSS}
        `;
        rows.push(
            <GridContainer css={rowCss} key={row} gap={0} isFullWidth={fields.isFullWidth}>
                {columns}
            </GridContainer>,
        );
    }

    return <>{rows}</>;
};

const defaultCustomCss = `& {

}`;

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const gridModule: WidgetModule = {
    component: Grid,
    definition: {
        group: "Basic",
        icon: "border-all",
        fieldDefinitions: [
            {
                name: fields.extraLargeColumnCount,
                displayName: "Extra Large Column Count",
                editorTemplate: "IntegerField",
                defaultValue: 4,
                fieldType: "General",
                sortOrder: 1,
                // XNG-Changes
                tooltip: `the width on screen with width greater than ${breakpoints.maxWidths[4]}px`,
            },
            {
                name: fields.extraLargeRowCount,
                displayName: "Extra Large Row Count",
                editorTemplate: "IntegerField",
                defaultValue: 4,
                fieldType: "General",
                sortOrder: 2,
                // XNG-Changes
                tooltip: `the width on screen with width greater than ${breakpoints.maxWidths[3]}px and less than or equal ${breakpoints.maxWidths[4]}px`,
            },
            {
                name: fields.largeColumnCount,
                displayName: "Large Width Column Count",
                editorTemplate: "IntegerField",
                defaultValue: 4,
                fieldType: "General",
                sortOrder: 3,
                // XNG-Changes
                tooltip: `the width on screen with width greater than ${breakpoints.maxWidths[2]}px and less than or equal ${breakpoints.maxWidths[3]}px`,
            },
            {
                name: fields.mediumColumnCount,
                displayName: "Medium Width Column Count",
                editorTemplate: "IntegerField",
                defaultValue: 1,
                fieldType: "General",
                sortOrder: 4,
                // XNG-Changes
                tooltip: `the width on screen with width greater than ${breakpoints.maxWidths[1]}px and less than or equal ${breakpoints.maxWidths[2]}px`,
            },
            {
                name: fields.smallColumnCount,
                displayName: "Small Width Column Count",
                editorTemplate: "IntegerField",
                defaultValue: 1,
                fieldType: "General",
                sortOrder: 5,
                // XNG-Changes
                tooltip: `the width on screen with width greater than ${breakpoints.maxWidths[0]}px and less than or equal ${breakpoints.maxWidths[1]}px`,
            },
            {
                name: fields.extraSmallColumnCount,
                displayName: "Extra Small Width Column Count",
                editorTemplate: "IntegerField",
                defaultValue: 1,
                fieldType: "General",
                sortOrder: 6,
                // XNG-Changes
                tooltip: `the width on a screen less than or equal${breakpoints.maxWidths[0]}px`,
            },
            {
                name: fields.isFullWidth,
                displayName: "Full Width",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: (_item, _advancedFeaturesEnabled, theme) => !!theme?.content?.isFullWidth,
                sortOrder: 7,
            },
            {
                name: "customCSS",
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default gridModule;
